import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Tag, Sparkles, TrendingUp, AlertCircle } from 'lucide-react';
import { Condition, CONDITION_LABELS } from '@crosova/shared';
import { api } from '../../lib/api';

interface PriceEstimate {
  low: number;
  mid: number;
  high: number;
  tip: string;
}

export function PriceAdvisorPage() {
  const [form, setForm] = useState({
    title: '', brand: '', category: '', condition: Condition.GOOD, size: '',
  });

  const [priceError, setPriceError] = useState('');

  const estimate = useMutation({
    mutationFn: () =>
      api.post<PriceEstimate>('/ai/estimate-price', form).then((r) => r.data),
    onSuccess: () => setPriceError(''),
    onError: (e: any) => setPriceError(e?.response?.data?.error ?? 'Price estimate failed. Check GEMINI_API_KEY is set in Railway.'),
  });

  function set(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const result = estimate.data;
  const hasResult = !!result && result.mid > 0;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-base font-semibold tracking-tight flex items-center gap-2">
          <Tag size={16} className="text-secondary-400" /> Price advisor
        </h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">
          AI-powered price suggestions based on UK marketplace sold data
        </p>
      </div>

      {priceError && (
        <div className="text-[12px] text-danger bg-danger/10 border border-danger/20 rounded-lg px-4 py-3">
          {priceError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Input form */}
        <div className="card p-5 space-y-3">
          <h2 className="text-[13px] font-semibold">Item details</h2>

          <Field label="Title / keywords">
            <input
              className="input"
              placeholder="e.g. Nike Air Max 90 trainers white"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand">
              <input className="input" placeholder="Nike" value={form.brand} onChange={(e) => set('brand', e.target.value)} />
            </Field>
            <Field label="Category">
              <input className="input" placeholder="Trainers" value={form.category} onChange={(e) => set('category', e.target.value)} />
            </Field>
          </div>

          <Field label="Size (optional)">
            <input className="input" placeholder="UK 10" value={form.size} onChange={(e) => set('size', e.target.value)} />
          </Field>

          <Field label="Condition">
            <div className="flex flex-wrap gap-1.5">
              {Object.values(Condition).map((c) => (
                <button
                  key={c}
                  onClick={() => set('condition', c)}
                  className={`flex-1 px-2 py-1.5 rounded-md text-[10.5px] border transition-colors ${
                    form.condition === c
                      ? 'border-primary-500/40 bg-primary-500/10 text-primary-300'
                      : 'border-white/[0.07] bg-white/[0.02] text-white/30'
                  }`}
                >
                  {CONDITION_LABELS[c]}
                </button>
              ))}
            </div>
          </Field>

          <button
            onClick={() => estimate.mutate()}
            disabled={!form.title.trim() || estimate.isPending}
            className="btn-primary w-full flex items-center justify-center gap-2 mt-1"
          >
            <Sparkles size={14} className={estimate.isPending ? 'animate-pulse' : ''} />
            {estimate.isPending ? 'Estimating…' : 'Get price estimate'}
          </button>

          <p className="text-[10px] text-white/20 text-center">
            Powered by AI — based on typical UK sold prices on eBay, Vinted & Depop
          </p>
        </div>

        {/* Result */}
        <div className="flex flex-col gap-3">
          {!hasResult && !estimate.isPending && !estimate.isError && (
            <div className="card p-8 flex flex-col items-center justify-center text-center flex-1">
              <TrendingUp size={32} className="text-white/10 mb-3" />
              <p className="text-sm text-white/30">Fill in the details and click<br />"Get price estimate"</p>
            </div>
          )}

          {estimate.isPending && (
            <div className="card p-8 flex flex-col items-center justify-center gap-3 flex-1">
              <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
              <p className="text-sm text-white/40">Analysing market data…</p>
            </div>
          )}

          {estimate.isError && (
            <div className="card p-5 flex items-start gap-3">
              <AlertCircle size={16} className="text-danger mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-danger">Estimate failed</p>
                <p className="text-[11px] text-white/40 mt-0.5">
                  Make sure your OpenAI API key is configured, then try again.
                </p>
              </div>
            </div>
          )}

          {hasResult && (
            <>
              {/* Price range */}
              <div className="card p-5">
                <div className="text-[11px] font-semibold text-white/30 uppercase tracking-wider mb-4">Suggested price range</div>
                <div className="flex items-end gap-3 mb-4">
                  <PriceBlock label="Conservative" pence={result.low} muted />
                  <PriceBlock label="Sweet spot" pence={result.mid} highlight />
                  <PriceBlock label="If lucky" pence={result.high} muted />
                </div>

                {/* Visual range bar */}
                <div className="relative h-2 bg-white/[0.05] rounded-full overflow-hidden">
                  {result.high > result.low && (
                    <div
                      className="absolute h-full bg-gradient-to-r from-primary-500/40 via-secondary-400 to-primary-500/40 rounded-full"
                      style={{
                        left: '0%',
                        width: '100%',
                      }}
                    />
                  )}
                  {/* Midpoint marker */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-secondary-400 rounded"
                    style={{
                      left: result.high > result.low
                        ? `${((result.mid - result.low) / (result.high - result.low)) * 100}%`
                        : '50%',
                    }}
                  />
                </div>
                <div className="flex justify-between text-[9.5px] text-white/25 mt-1">
                  <span>£{(result.low / 100).toFixed(2)}</span>
                  <span>£{(result.high / 100).toFixed(2)}</span>
                </div>
              </div>

              {/* Tip */}
              {result.tip && (
                <div className="card p-4 flex items-start gap-2.5">
                  <Sparkles size={14} className="text-secondary-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-white/60 leading-relaxed">{result.tip}</p>
                </div>
              )}

              {/* Disclaimer */}
              <p className="text-[10px] text-white/20 px-1">
                AI estimates only — actual prices vary by demand, photos, and listing quality.
                Always check recently sold items on each platform before pricing.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceBlock({ label, pence, highlight, muted }: { label: string; pence: number; highlight?: boolean; muted?: boolean }) {
  return (
    <div className={`flex-1 rounded-xl p-3 border text-center ${
      highlight
        ? 'border-secondary-400/30 bg-secondary-400/[0.06]'
        : 'border-white/[0.06] bg-white/[0.02]'
    }`}>
      <div className="text-[10px] text-white/30 mb-1">{label}</div>
      <div className={`text-[20px] font-semibold tracking-tight ${highlight ? 'text-secondary-400' : muted ? 'text-white/50' : 'text-white'}`}>
        £{(pence / 100).toFixed(2)}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-white/35 mb-1">{label}</div>
      {children}
    </div>
  );
}
